import React, { useEffect, useMemo, useState } from "react";
import { useRoom } from "./RoomProvider";
import { useAttentionQueue } from "../../hooks/useAttentionQueue";
import { useAuth } from "../../lib/auth";
import { apiClient } from "../../lib/api";

/**
 * ShiftHandoff — F5 · Nova briefs the incoming VA in 30 seconds.
 *
 * Summoned via a button in the FloorMark corner. Generates a text summary
 * of the current room state from the same hooks the Pulse Panel uses. Meant
 * to be read aloud (or copy/pasted into a chat) at shift change.
 */
export default function ShiftHandoff() {
  const [open, setOpen] = useState(false);
  const { allTrips = [], identity } = useRoom();
  const { user } = useAuth();
  const q = useAttentionQueue();
  const [myShift, setMyShift] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    apiClient.get("/floor/monitoring/live").then(({ data }) => {
      if (!alive) return;
      const mine = (data?.items || []).find((s) => s.email === user?.email);
      setMyShift(mine || null);
    }).catch(() => setMyShift(null));
    return () => { alive = false; };
  }, [open, user?.email]);

  const brief = useMemo(() => {
    const inCycle = (allTrips || []).filter(t =>
      ["ACCEPTED","EN_ROUTE","ARRIVED_PU","ON_TRIP","ARRIVED_DO"].includes(String(t.status).toUpperCase())
    ).length;
    const pending = (allTrips || []).filter(t =>
      ["REQUESTED","TP_ACCEPTED","EXPIRED_OFFER"].includes(String(t.status).toUpperCase())
    ).length;
    const coworker = identity?.coworker_name || "Nova";
    const lines = [
      `${coworker} handoff · ${new Date().toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})}`,
      ``,
      `In cycle:      ${inCycle} trip${inCycle===1?"":"s"}`,
      `Pending:       ${pending}`,
      `Needs you:     ${q.counts.needs}`,
      `Risks:         ${q.counts.risks}`,
      `Late drivers:  ${q.counts.late}`,
      `Failing TPs:   ${q.counts.failing_tps}`,
      ``,
      q.nextAction ? `Next action:   ${(q.nextAction.verb || "handle").toUpperCase()} — ${q.nextAction.label}` : `Next action:   nothing pressing`,
      ``,
      `AI · last 5m:`,
      ...(q.aiActions.slice(0, 5).map(a => `  · ${a.text}`)),
    ];
    return lines.join("\n");
  }, [allTrips, q, identity]);

  const shiftLine = myShift
    ? `My shift so far: ${Math.round((Date.now() - new Date(myShift.started_at).getTime()) / 60000)}m on The Floor · ${myShift.totals?.keypresses ?? 0} keys · ${myShift.totals?.clicks ?? 0} clicks`
    : null;
  const fullBrief = shiftLine ? `${brief}\n\n${shiftLine}` : brief;

  return (
    <>
      <button className="handoff-btn" onClick={() => setOpen(true)} data-testid="shift-handoff-btn">
        handoff
      </button>
      {open && (
        <div className="handoff-modal" onClick={() => setOpen(false)}>
          <div className="handoff-card" onClick={(e) => e.stopPropagation()} data-testid="shift-handoff-card">
            <div className="handoff-title">SHIFT HANDOFF · 30-SECOND BRIEF</div>
            <pre className="handoff-body">{fullBrief}</pre>
            <div className="handoff-actions">
              <button className="handoff-copy"
                      onClick={() => { navigator.clipboard.writeText(fullBrief); }}>
                copy to clipboard
              </button>
              <button className="handoff-close" onClick={() => setOpen(false)}>done</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
